import type { ReactNode } from 'react';
import { EditableAvatar } from './EditableAvatar';

/**
 * The identity block at the top of a `<ProfileShell>` sidebar — who or what this record is,
 * centred above the navigation.
 *
 * Everything below the name is optional and rendered only when supplied, so a record with
 * two facts about it does not leave a column of em-dashes. Facts that need a value or
 * nothing belong here; facts that need a label belong in the panel's field table.
 */
export function ProfileIdentity({
  name,
  avatarUrl,
  onEditAvatar,
  onPreviewAvatar,
  email,
  title,
  meta,
  code,
  status,
  actions,
  id,
}: {
  /** Drives both the heading and the avatar's initials. */
  name: string;
  /** A photograph for the avatar. Its absence is what falls back to the initials. */
  avatarUrl?: string | null;
  /**
   * Puts **Edit** in the avatar's overlay menu, which opens on hover and on keyboard focus.
   *
   * **Its absence is what removes the entry**, rather than a disabled one — the
   * hide-don't-disable rule, so a caller passes it only when the reader holds the key that
   * writes the image. With neither callback there is no overlay at all.
   */
  onEditAvatar?: () => void;
  /** Puts **Preview** in that menu. Disabled for state while there is no photograph. */
  onPreviewAvatar?: () => void;
  email?: string | null;
  /** One line under the name — a role, a department, a classification. */
  title?: ReactNode;
  /** A second, quieter line. */
  meta?: ReactNode;
  /** A machine identifier, rendered monospaced. */
  code?: string | null;
  /** The status pill. */
  status?: ReactNode;
  /** Action chips, laid out in a row under the identity. */
  actions?: ReactNode;
  id?: string;
}) {
  const showEmail = Boolean(email) && !sameValue(email, name);

  return (
    <div id={id} className="flex flex-col items-center gap-2 border-b border-slate-200 px-4 py-6">
      {/* Grey, not brand: the brand disc is the signed-in identity in the app bar, and a
          second one on the page it is looking at competes with the record. */}
      <EditableAvatar
        id={`${id ?? 'profile'}-avatar`}
        name={name}
        email={email}
        imageUrl={avatarUrl}
        size="2xl"
        onEdit={onEditAvatar}
        onPreview={onPreviewAvatar}
      />
      <h2 className="mt-1 min-w-0 break-words text-center text-sm text-slate-800">{name}</h2>

      {/*
        `name` is usually a display name that FALLS BACK to the email, so on any record
        without a name the two lines would be the same string printed twice. The guard
        lives here rather than at each call site: a caller cannot know what the fallback
        resolved to, and the one that forgets is the one that ships it.
      */}
      {showEmail ? (
        <p className="min-w-0 break-all text-center font-mono text-xs text-slate-500">{email}</p>
      ) : null}

      {title ? <p className="text-center text-sm text-slate-600">{title}</p> : null}

      {meta ? <p className="text-center text-xs text-slate-500">{meta}</p> : null}

      {code ? (
        <p className="min-w-0 break-all text-center font-mono text-xs text-slate-400">{code}</p>
      ) : null}

      {status ? <div className="mt-0.5">{status}</div> : null}

      {actions ? <div className="mt-2 flex flex-wrap justify-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Case- and whitespace-insensitive, because an email is not case-sensitive. */
function sameValue(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
}
